// DefenderPassthroughIpAllowlistManager — admin page at /settings/benign-ip-allowlist.
// Backs the Microsoft-security pass-through rule's `skip_if_in_defender_passthrough_ip_allowlist`
// tier: a GLOBAL cross-tenant allowlist of benign IPs that Microsoft mis-flags as malicious
// (e.g. a SaaS app's shared egress). When the rule toggle is on, a pass-through alert is dropped
// if its source IP (sign-in IP or device IP) is on this list. A known-malicious IP still alerts
// (override_if_malicious_ip punches through). Read-open, admin-write. Soft-delete (enabled=false).
// Every write bumps a __version sentinel the detection pipeline polls (~30s).
// Identity = (PartitionKey = 'global', RowKey = canonical IP) — the IP is immutable.

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import ipaddr from 'ipaddr.js';
import {
  fetchDefenderPassthroughIpAllowlist,
  addDefenderPassthroughIpEntry,
  updateDefenderPassthroughIpEntry,
  removeDefenderPassthroughIpEntry,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Badge from './ui/Badge';
import Pagination from './Pagination';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr), 'MMM d, yyyy'); } catch { return dateStr; }
}

// Canonicalize an IP to match the pipeline EXACTLY: str(ipaddress.ip_address(...)).
// IPv4 unchanged; IPv6 compressed + lowercased (RFC 5952). Use parse().toString() —
// NOT toNormalizedString() (does NOT compress: 2001:db8:0:0:0:0:0:1) and NOT process()
// (collapses IPv4-mapped IPv6 to dotted IPv4, diverging from Python). Returns { ok, canonical }
// or { ok:false, error }.
export function tryCanonicalizeIp(raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return { ok: false, error: 'IP address is required.' };
  if (!ipaddr.isValid(s)) return { ok: false, error: `'${String(raw).trim()}' is not a valid IP address.` };
  try {
    return { ok: true, canonical: ipaddr.parse(s).toString().toLowerCase() };
  } catch {
    return { ok: false, error: `'${String(raw).trim()}' is not a valid IP address.` };
  }
}

// Common Microsoft Graph security alert serviceSources (informational; not matched on).
const SERVICE_SOURCES = [
  'azureAdIdentityProtection',
  'microsoftDefenderForEndpoint',
  'microsoftDefenderForOffice365',
  'microsoftDefenderForIdentity',
  'microsoftDefenderForCloudApps',
  'microsoftDefenderForCloud',
  'microsoft365Defender',
  'microsoftAppGovernance',
  'dataLossPrevention',
];

const rowId = (r) => r.ip || r.rowKey;

export default function DefenderPassthroughIpAllowlistManager() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');
  const [showDisabled, setShowDisabled] = useState(true);

  const [formIp, setFormIp] = useState('');
  const [formReason, setFormReason] = useState('');
  const [formSource, setFormSource] = useState('azureAdIdentityProtection');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [editSource, setEditSource] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [sortKey, setSortKey] = useState('addedUtc');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Resizable columns: IP, Reason, Source service, Enabled, Added, Added By, Actions
  const [colWidths, setColWidths] = useState([220, 320, 200, 100, 120, 180, 120]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDefenderPassthroughIpAllowlist();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load benign-IP allowlist');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let r = rows;
    if (!showDisabled) r = r.filter((x) => x.enabled !== false);
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      r = r.filter((x) =>
        (x.ip && x.ip.toLowerCase().includes(q)) ||
        (x.reason && x.reason.toLowerCase().includes(q)) ||
        (x.sourceService && x.sourceService.toLowerCase().includes(q))
      );
    }
    return r;
  }, [rows, showDisabled, filterQuery]);

  const sorted = useMemo(() => {
    const result = [...filtered];
    result.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (av === bv) return 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      return sortDir === 'asc' ? 1 : -1;
    });
    return result;
  }, [filtered, sortKey, sortDir]);

  useEffect(() => { setCurrentPage(1); }, [filterQuery, showDisabled, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const paged = sorted.slice(startIdx, startIdx + pageSize);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIndicator = ({ k }) => {
    if (sortKey !== k) return <span className="text-gray-700 ml-1">{'↕'}</span>;
    return sortDir === 'asc' ? <ChevronUpIcon className="inline h-3 w-3 ml-1" /> : <ChevronDownIcon className="inline h-3 w-3 ml-1" />;
  };

  // Live add-form canonicalization preview.
  const canon = useMemo(() => tryCanonicalizeIp(formIp), [formIp]);
  const ipLooksValid = canon.ok;
  const reasonLooksValid = formReason.trim().length > 0;

  const handleAdd = async (e) => {
    e.preventDefault();
    const c = tryCanonicalizeIp(formIp);
    if (!c.ok) { setAddError(c.error); return; }
    if (!reasonLooksValid) { setAddError('Reason is required.'); return; }
    if (formReason.length > 2000) { setAddError('Reason exceeds 2000 chars.'); return; }

    setAdding(true);
    setAddError(null);
    try {
      const created = await addDefenderPassthroughIpEntry({
        ip: c.canonical,
        reason: formReason.trim(),
        sourceService: formSource.trim(),
        enabled: true,
      });
      setRows((prev) => {
        const ix = prev.findIndex((r) => rowId(r) === rowId(created));
        if (ix >= 0) { const next = [...prev]; next[ix] = created; return next; }
        return [created, ...prev];
      });
      setFormIp(''); setFormReason('');
    } catch (err) {
      setAddError(err.message || 'Failed to add entry');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (row) => {
    setEditError(null);
    setConfirmRemove(null);
    setEditingId(rowId(row));
    setEditReason(row.reason || '');
    setEditSource(row.sourceService || '');
  };

  const cancelEdit = () => { setEditingId(null); setEditError(null); };

  const handleSaveEdit = async (row) => {
    if (!editReason.trim()) { setEditError('Reason is required.'); return; }
    if (editReason.length > 2000) { setEditError('Reason exceeds 2000 chars.'); return; }
    setSavingEdit(true);
    setEditError(null);
    try {
      const updated = await updateDefenderPassthroughIpEntry(row.ip, {
        reason: editReason.trim(),
        sourceService: editSource.trim(),
      });
      setRows((prev) => prev.map((x) => (rowId(x) === rowId(row) ? { ...x, ...updated } : x)));
      cancelEdit();
    } catch (err) {
      setEditError(err.message || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleEnabled = async (row) => {
    setTogglingId(rowId(row));
    try {
      const updated = await updateDefenderPassthroughIpEntry(row.ip, { enabled: !row.enabled });
      setRows((prev) => prev.map((x) => (rowId(x) === rowId(row) ? { ...x, ...updated } : x)));
    } catch (err) {
      setError(err.message || 'Failed to toggle entry');
    } finally {
      setTogglingId(null);
    }
  };

  const handleRemove = async (row) => {
    setRemoving(true);
    try {
      await removeDefenderPassthroughIpEntry(row.ip);
      setRows((prev) => prev.map((x) => (rowId(x) === rowId(row) ? { ...x, enabled: false } : x)));
      setConfirmRemove(null);
    } catch (err) {
      setError(err.message || 'Failed to disable entry');
    } finally {
      setRemoving(false);
    }
  };

  const cols = [
    { label: 'IP',             sortKey: 'ip' },
    { label: 'Reason',         sortKey: null },
    { label: 'Source service', sortKey: 'sourceService' },
    { label: 'Enabled',        sortKey: 'enabled' },
    { label: 'Added',          sortKey: 'addedUtc' },
    { label: 'Added By',       sortKey: 'addedBy' },
    { label: 'Actions',        sortKey: null },
  ];

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheckIcon className="h-7 w-7 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-100">Defender Pass-through IP Allowlist</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Global benign IPs that suppress the <strong>Microsoft-security pass-through</strong> rules
          (Defender / Entra ID Protection). When a pass-through alert's source IP — sign-in IP or device IP —
          matches an enabled entry, the alert is dropped. Use this to silence IPs Microsoft <em>mis-flags</em> as
          malicious (e.g. a SaaS app's shared egress). It is the analyst counterpart to the Known Malicious IPs
          list — one entry clears the false positive across every tenant, and a <strong>genuinely-malicious IP
          still alerts</strong>. Exact-IP only (no CIDR ranges).
        </p>
      </div>

      {isAdmin ? (
        <div className="card mb-6">
          <h2 className="text-sm font-semibold text-gray-300 mb-4">Add Pass-through Benign IP</h2>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <div className="w-full lg:w-72">
                <label className="block text-xs text-gray-500 mb-1">IP address</label>
                <input
                  type="text"
                  placeholder="e.g. 52.22.129.156"
                  value={formIp}
                  onChange={(e) => setFormIp(e.target.value)}
                  className="input-field h-10 py-0 font-mono text-xs"
                  required
                />
                {formIp.trim() && (
                  ipLooksValid ? (
                    <p className="text-xs text-gray-500 mt-1">Stored as: <span className="font-mono text-gray-400">{canon.canonical}</span></p>
                  ) : (
                    <p className="text-xs text-red-400 mt-1">{canon.error}</p>
                  )
                )}
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Reason</label>
                <input
                  type="text"
                  placeholder="why this IP is trusted (e.g. OncoEMR SaaS egress; MS FP)"
                  value={formReason}
                  onChange={(e) => setFormReason(e.target.value)}
                  className="input-field h-10 py-0"
                  required
                />
              </div>
              <div className="w-full lg:w-64">
                <label className="block text-xs text-gray-500 mb-1">Source service (optional)</label>
                <input
                  type="text"
                  list="passthrough-ip-service-sources"
                  placeholder="azureAdIdentityProtection"
                  value={formSource}
                  onChange={(e) => setFormSource(e.target.value)}
                  className="input-field h-10 py-0 font-mono text-xs"
                />
                <datalist id="passthrough-ip-service-sources">
                  {SERVICE_SOURCES.map((s) => <option key={s} value={s} />)}
                </datalist>
              </div>
              <div className="flex-shrink-0">
                <button type="submit" disabled={adding || !ipLooksValid || !reasonLooksValid} className="btn-primary flex items-center gap-2 whitespace-nowrap">
                  {adding ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
                  Add Entry
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-600">
              Identity is the canonical IP. Re-adding the same IP is an idempotent upsert. To change the IP,
              disable the old entry and add a new one. A <strong>known-malicious IP still alerts</strong>.
            </p>
          </form>
          {addError && <p className="mt-2 text-sm text-red-400">{addError}</p>}
        </div>
      ) : (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to add, edit, or disable entries.</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      {editError && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{editError}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Filter by IP, reason, source service…"
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className="input-field pl-9"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
              className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
            />
            Show disabled
          </label>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span>{filtered.length} entr{filtered.length !== 1 ? 'ies' : 'y'}</span>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table ref={tableRef} className="text-sm" style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}>
            <thead>
              <tr className="border-b border-gray-800">
                {cols.map((col, colIdx) => (
                  <th
                    key={col.label}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    onClick={col.sortKey ? () => toggleSort(col.sortKey) : undefined}
                    className={`${colIdx === cols.length - 1 ? 'text-center' : 'text-left'} text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none ${col.sortKey ? 'cursor-pointer hover:text-gray-300' : ''}`}
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <span className="truncate">{col.label}</span>
                    {col.sortKey && <SortIndicator k={col.sortKey} />}
                    <div className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group" onMouseDown={(e) => { e.stopPropagation(); startResize(e, colIdx); }}>
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={cols.length} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span className="text-gray-500 text-sm">Loading allowlist…</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={cols.length} className="px-6 py-12 text-center">
                    <ShieldCheckIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No pass-through benign-IP allowlist entries</p>
                    <p className="text-gray-600 text-sm mt-1">
                      {filterQuery || !showDisabled ? 'Try adjusting your filters.' : isAdmin ? 'Add an entry above to get started.' : 'No entries yet.'}
                    </p>
                  </td>
                </tr>
              )}

              {!loading && paged.map((r) => {
                const isEditing = editingId === rowId(r);
                const isEnabled = r.enabled !== false;
                return (
                <tr key={rowId(r)} className={`hover:bg-gray-800/50 transition-colors ${isEnabled ? '' : 'opacity-60'}`}>
                  <td className="px-4 py-3 truncate overflow-hidden" title={r.ip}>
                    <span className="font-mono text-xs text-gray-300">{r.ip}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 truncate overflow-hidden" title={r.reason || ''}>
                    {isEditing ? (
                      <input type="text" value={editReason} onChange={(e) => setEditReason(e.target.value)} className="input-field w-full text-xs py-1" placeholder="reason" />
                    ) : (r.reason || '—')}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={r.sourceService || ''}>
                    {isEditing ? (
                      <input type="text" list="passthrough-ip-service-sources" value={editSource} onChange={(e) => setEditSource(e.target.value)} className="input-field w-full text-xs py-1 font-mono" />
                    ) : (r.sourceService ? <span className="font-mono">{r.sourceService}</span> : '—')}
                  </td>
                  <td className="px-4 py-3">
                    {isAdmin ? (
                      <button onClick={() => handleToggleEnabled(r)} disabled={togglingId === rowId(r)} title={isEnabled ? 'Click to disable' : 'Click to enable'} className="focus:outline-none">
                        <Badge tone={isEnabled ? 'success' : 'neutral'}>{isEnabled ? 'Enabled' : 'Disabled'}</Badge>
                      </button>
                    ) : (
                      <Badge tone={isEnabled ? 'success' : 'neutral'}>{isEnabled ? 'Enabled' : 'Disabled'}</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs truncate overflow-hidden">{formatDate(r.addedUtc)}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={r.addedBy || ''}>{r.addedBy || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {!isAdmin ? (
                      <span className="text-gray-700">{'—'}</span>
                    ) : isEditing ? (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleSaveEdit(r)} disabled={savingEdit} className="text-gray-600 hover:text-green-400 transition-colors p-1" title="Save">
                          {savingEdit ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                        </button>
                        <button onClick={cancelEdit} disabled={savingEdit} className="text-gray-600 hover:text-gray-300 transition-colors p-1" title="Cancel">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : confirmRemove === rowId(r) ? (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleRemove(r)} disabled={removing} className="btn-danger px-2 py-1 text-xs">
                          {removing ? 'Disabling…' : 'Confirm'}
                        </button>
                        <button onClick={() => setConfirmRemove(null)} className="btn-secondary px-2 py-1 text-xs">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => startEdit(r)} className="text-gray-600 hover:text-blue-400 transition-colors p-1" title="Edit reason / source service (the IP is immutable — disable and re-add to change it)">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => setConfirmRemove(rowId(r))} disabled={!isEnabled} className="text-gray-600 hover:text-red-400 transition-colors p-1 disabled:opacity-30 disabled:cursor-not-allowed" title={isEnabled ? 'Disable (soft-delete) — pass-through alerts from this IP will resume firing on the next run' : 'Already disabled'}>
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      </div>
    </div>
  );
}
